/**
 * Framework-agnostic pub/sub for the `auth:token-expired` window event.
 *
 * Matches the event name used consistently across every Colony app so that
 * existing listeners (AuthContext, router guards, axios interceptors) continue
 * to work after the migration without any changes.
 *
 * All methods are SSR-safe — they no-op when `window` (or the method) is absent.
 * They do NOT catch: `addEventListener` / `removeEventListener` / `dispatchEvent`
 * cannot fail at runtime in a browser (DOM Standard, "inner invoke": a listener's
 * own exception is REPORTED, never thrown out of `dispatchEvent`), so a throw here is a
 * defect and surfaces (SWALLOWED-CATCH-BURNDOWN-001 U3 — the old catches cited
 * only "some test harnesses throw").
 *
 * @module @puffin-core/auth-token-storage/expiryEvents
 */

const EVENT_NAME = 'auth:token-expired';

/**
 * Create a per-instance expiry event bus.
 *
 * @returns {{
 *   on: (callback: (detail: object) => void) => () => void,
 *   emit: (detail?: object) => void,
 *   reset: () => void,
 * }}
 */
export function createExpiryEvents() {
  const handlers = new Set();

  const removeHandler = (handler) => {
    if (typeof window === 'undefined' || !window.removeEventListener) {
      handlers.delete(handler);
      return;
    }
    window.removeEventListener(EVENT_NAME, handler);
    handlers.delete(handler);
  };

  return {
    /**
     * Subscribe to `auth:token-expired` events.
     * Returns an unsubscribe function. Safe to call multiple times.
     *
     * @param {(detail: object) => void} callback
     * @returns {() => void} unsubscribe
     */
    on(callback) {
      if (typeof window === 'undefined' || !window.addEventListener) {
        return () => {};
      }
      const handler = (event) => callback(event.detail);
      window.addEventListener(EVENT_NAME, handler);
      handlers.add(handler);
      return () => removeHandler(handler);
    },

    /**
     * Dispatch an `auth:token-expired` CustomEvent with the given detail.
     * No-op in SSR.
     *
     * @param {object} [detail={}]
     */
    emit(detail = {}) {
      if (typeof window === 'undefined' || !window.dispatchEvent) {
        return;
      }
      window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail }));
    },

    /**
     * Remove every registered handler. Test-only.
     */
    reset() {
      const snapshot = Array.from(handlers);
      snapshot.forEach(removeHandler);
    },
  };
}
